import { Layout, Space, Menu, Flex, Button, Dropdown, Spin, Modal } from 'antd';
import { useState, React, useEffect } from 'react';
import {
  LogoutOutlined,
  DownOutlined,
  UserOutlined,
  ReloadOutlined,
  CloseCircleFilled,
} from '@ant-design/icons';
import moment from 'moment-timezone';
import HomeRouter from './../../router/HomeRouter';
import { getMenuItems } from './home/menuItem';
import { HttpHandler, storeUserInfo } from '../../shared/httpHandler';
import IrentLogo from './../../images/IrentLogo.png';
import { useMsalAuthentication, useIsAuthenticated } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { msalAuthService, userInfoKeys } from '../../shared/msalAuthService';
import ChargingHttpHandler from '../../shared/chargingHttpHandler';
import secureStorage from '../../shared/secureStorage';

const Home = () => {
  // 處理 MSAL 登入驗證機制
  const isMsalAuthenticated = useIsAuthenticated();
  const { result: msalAuthResult, error: msalAuthError } =
    useMsalAuthentication(InteractionType.Redirect, {
      ...loginRequest,
    });
  const [loginError, setLoginError] = useState(false);
  const [loginErrorMsg, setLoginErrorMsg] = useState('');
  const [isFullAuthenticated, setIsFullAuthenticated] = useState(false);

  const handleLogout = () => {
    // MSAL 登出
    msalAuthService.logout();
  };

  const httpHandler = new HttpHandler();
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);
  const items = [
    {
      key: 'Setting',
      label: secureStorage.getItem('userAccount'),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      label: <a onClick={handleLogout}>登出</a>,
      key: 'logout',
      icon: <LogoutOutlined />,
    },
  ];

  const updateMenu = () => {
    setMenuLoading(true);
    const menuItems = getMenuItems();
    setMenuItems(menuItems);
    setMenuLoading(false);
  };

  // 使用 MSAL token 登入後端
  const loginBackendWithMsalToken = async () => {
    httpHandler
      .post('/auth/login_with_msal', {}, 'application/json')
      .then(async (response) => {
        if (response.success) {
          const userInfo = response.data;
          storeUserInfo(userInfo);

          try {
            // 1. 先拿 charging access token~~
            const chargingHttpHandler = new ChargingHttpHandler(false);
            const response = await chargingHttpHandler.post(
              '/platform/ad_login',
              {
                user_account: userInfo.account,
              },
              'application/json'
            );
            console.log('ad_login response', response);

            if (
              response &&
              response.success &&
              response.data &&
              response.data.access_token
            ) {
              const chargingAccessToken = response.data.access_token;
              secureStorage.setItem('chargingAccessToken', chargingAccessToken);

              // 2. 拿使用者的RoleID~~
              const roleResponse = await chargingHttpHandler.axios().post(
                '/platform/search_user_name',
                {
                  user_account: userInfo.account,
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${chargingAccessToken}`,
                  },
                }
              );

              // 3. 設定使用者的角色～
              const roleId = roleResponse.data?.user_name;
              const check = roleResponse.data?.check;
              secureStorage.setItem('userRole', roleId);
              secureStorage.setItem('check', check);

              // 4. 紀錄Log!
              const loginTime = secureStorage.getItem('userLoginTime');
              await chargingHttpHandler.axios().post(
                '/platform/update_user_log_table',
                {
                  log_time: moment().format('YYYY/MM/DD HH:mm:ss'),
                  user_role: roleId,
                  action_type: '登入',
                  action_content: '用戶登入系統',
                  status: 1,
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${chargingAccessToken}`,
                  },
                }
              );

              // 5. 更新使用者最後登錄的時間
              await chargingHttpHandler.axios().post(
                '/platform/update_user_last_login_time',
                {
                  user_account: userInfo.account,
                  last_login_time: moment
                    .unix(loginTime)
                    .format('YYYY/MM/DD HH:mm:ss'),
                },
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${chargingAccessToken}`,
                  },
                }
              );
            } else {
              console.error('Failed to get charging access token:', response);
            }
          } catch (error) {
            console.error('Failed to setup charging system:', error);
          }

          setIsFullAuthenticated(true);
          return;
        }
        console.error('loginWithMSAL error', response);
        setLoginError(true);
        setLoginErrorMsg(response.message);
      })
      .catch((error) => {
        console.error('loginWithMSAL error', error);
        setLoginError(true);
      })
      .finally(() => {
        updateMenu();
      });
  };

  useEffect(() => {
    if (msalAuthResult) {
      // 使用 MSAL token 派發時間作為使用者登入時間
      const iat = msalAuthResult?.idTokenClaims?.iat;
      secureStorage.setItem('userLoginTime', iat);

      // 取得必要資訊
      const userAccount = secureStorage.getItem('userAccount');
      const userRole = secureStorage.getItem('userRole');
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
      const chargingHttpHandler = new ChargingHttpHandler(false);

      // 更新最後登入時間
      if (userAccount && chargingAccessToken) {
        chargingHttpHandler
          .axios()
          .post(
            '/platform/update_user_last_login_time',
            {
              user_account: userAccount,
              last_login_time: moment.unix(iat).format('YYYY/MM/DD HH:mm:ss'),
            },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${chargingAccessToken}`,
              },
            }
          )
          .catch((e) => {
            console.error('更新最後登入時間失敗', e);
          });
      }

      // 同步寫入登入紀錄
      if (userRole && chargingAccessToken) {
        chargingHttpHandler
          .axios()
          .post(
            '/platform/update_user_log_table',
            {
              log_time: moment().format('YYYY/MM/DD HH:mm:ss'),
              user_role: userRole,
              action_type: '登入',
              action_content: '用戶登入系統',
              status: 1,
            },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${chargingAccessToken}`,
              },
            }
          )
          .catch((e) => {
            console.error('寫入登入紀錄失敗', e);
          });
      }
    }
  }, [msalAuthResult]);

  useEffect(() => {
    if (msalAuthError) {
      console.error('msalAuthError:', msalAuthError);
    }
  }, [msalAuthError]);

  useEffect(() => {
    if (isMsalAuthenticated && msalAuthResult) {
      // 獲取所有以 'user' 開頭的 localStorage 項目
      const localUserInfo = Object.keys(secureStorage)
        .filter((key) => key.startsWith('user'))
        .reduce((obj, key) => {
          obj[key] = secureStorage.getItem(key);
          return obj;
        }, {});

      // 檢查是否所有必要的使用者資訊都存在於 localStorage 中
      const hasAllUserInfo = userInfoKeys.every(
        (key) =>
          localUserInfo.hasOwnProperty(key) &&
          localUserInfo[key] !== null &&
          localUserInfo[key] !== undefined
      );

      // 檢查是否依然處於同一個 session
      const tokenIat = msalAuthResult?.idTokenClaims?.iat;
      const isSameSession = tokenIat === parseInt(localUserInfo.userLoginTime);

      // 檢查充電平台 token 是否有效
      const chargingAccessToken = secureStorage.getItem('chargingAccessToken');
      const userRole = secureStorage.getItem('userRole');

      // 解析 token 檢查過期時間
      let isChargingTokenValid = false;
      if (chargingAccessToken) {
        try {
          const tokenPayload = JSON.parse(
            atob(chargingAccessToken.split('.')[1])
          );
          const tokenExp = tokenPayload.exp;
          const currentTime = Math.floor(Date.now() / 1000);

          // 如果 token 還有超過 5 分鐘的有效期，則視為有效 反之則視為無效
          isChargingTokenValid = tokenExp - currentTime > 300;
          // console.log('Charging token expires in:', Math.floor((tokenExp - currentTime) / 60), 'minutes');
        } catch (error) {
          console.error('Error parsing charging token:', error);
          isChargingTokenValid = false;
        }
      }
      // console.log('isChargingTokenValid', isChargingTokenValid);
      // console.log('chargingAccessToken', chargingAccessToken);
      const hasChargingInfo =
        chargingAccessToken && userRole && isChargingTokenValid;

      if (hasAllUserInfo && isSameSession && hasChargingInfo) {
        // 如果所有使用者資訊都存在且處於同一個 session，則直接使用現有資訊
        updateMenu();
        setIsFullAuthenticated(true);
      } else {
        // 若使用者資訊不全或處於不同 session，則重新登入
        loginBackendWithMsalToken();
      }
    }
  }, [isMsalAuthenticated, msalAuthResult]);

  // useEffect(() => {
  //   // 印出目前登入者的功能權限
  //   try {
  //     const userFunctionTree = JSON.parse(localStorage.getItem('userFunctionTree') || '[]');
  //     console.log('userFunctionTree:', userFunctionTree);
  //   } catch (e) {
  //     console.log('userFunctionTree parse error', e);
  //   }
  // }, []);

  const { Header, Content, Footer, Sider } = Layout;
  const [siderCollapse, setSiderCollapse] = useState(false);

  function hasChargingAppBackend(tree) {
    if (!Array.isArray(tree)) return false;
    for (const node of tree) {
      if (node.id === 18) return true;
      if (node.children && hasChargingAppBackend(node.children)) return true;
    }
    return false;
  }

  const userFunctionTree = JSON.parse(
    secureStorage.getItem('userFunctionTree') || '[]'
  );
  const showChargingAppTitle = hasChargingAppBackend(userFunctionTree);
  // console.log('userFunctionTree', userFunctionTree);
  return (
    <>
      {msalAuthError || loginError ? (
        <Modal
          title={
            <>
              <CloseCircleFilled
                style={{
                  color: '#ff4d4f',
                  fontSize: '18px',
                  marginRight: '8px',
                }}
              />
              登入驗證失敗！
            </>
          }
          closable={false}
          centered={true}
          open={true}
          footer={
            <Button
              type="primary"
              className="button--delete"
              icon={<ReloadOutlined />}
              onClick={() => window.location.reload()}>
              重新登入
            </Button>
          }>
          <p>{msalAuthError ? msalAuthError : 'MSAL 登入驗證失敗'}</p>
          <p>{loginErrorMsg ? loginErrorMsg : '登入驗證失敗，請重新嘗試。'}</p>
        </Modal>
      ) : (
        <></>
      )}
      <Spin
        tip="Loading..."
        size="large"
        spinning={!isFullAuthenticated}
        fullscreen
      />
      <Layout className="home" theme="light">
        <Sider
          collapsible="true"
          collapsed={siderCollapse}
          onCollapse={() => {
            setSiderCollapse(!siderCollapse);
          }}
          theme="dark">
          {menuLoading ? (
            <Spin className="menu-loading-icon" />
          ) : (
            <Menu
              theme="dark"
              mode="inline"
              // openKeys={[2]}
              items={menuItems}></Menu>
          )}
        </Sider>
        <Layout>
          <Header className="home-header">
            <div className="companyname">
              <Space>
                <img src={IrentLogo} alt="Logo" className="h-8" />
                {showChargingAppTitle ? '充電樁APP後台' : 'LINE專案後台'}
              </Space>
            </div>
            <div>
              <Space>
                <Dropdown menu={{ items }} placement="bottomRight">
                  <Button>
                    <UserOutlined />
                    {secureStorage.getItem('userName')}
                    <DownOutlined />
                  </Button>
                </Dropdown>
              </Space>
            </div>
          </Header>
          <Content className="home-content">
            <HomeRouter />
          </Content>
          <Footer>
            <Flex justify="space-between">
              <span>Copyright © 2022-2023 iRent. All rights reserved.</span>
              <span className="login-info">
                {' '}
                登入時間:
                {moment
                  .unix(secureStorage.getItem('userLoginTime'))
                  .format('YYYY/MM/DD HH:mm:ss')}
              </span>
            </Flex>
          </Footer>
        </Layout>
      </Layout>
    </>
  );
};

export default Home;
